import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { ClientReferralTasksService } from 'app/core/services/client-referral-tasks.service';
import { ClientReferralTask } from 'app/core/models/client-referral-task.model';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { UserPresenceService } from 'app/core/services/user-presence.service';

import { ClientTaskNavComponent } from '../../components/client-task-nav/client-task-nav.component';
import { ClientReferralTaskHeaderComponent } from '../../components/client-referral-task-header/client-referral-task-header.component';
import { PatientPrescriberActiveNoteComponent } from '../../components/patient-prescriber-active-note/patient-prescriber-active-note.component';
import { ClientReferralTaskVerificationComponent } from '../../components/client-referral-task-verification/client-referral-task-verification.component';
import { ClientReferralTaskDetailsComponent } from '../../components/client-referral-task-details/client-referral-task-details.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

const shouldReloadTask = (task: ClientReferralTask, taskId) => task && taskId !== task.id.toString();
const userPresenceKey = 'clientReferralTask';

@Component({
    templateUrl: './client-referral-task.component.html',
    imports: [ClientTaskNavComponent,
              ClientReferralTaskHeaderComponent,
              PatientPrescriberActiveNoteComponent,
              ClientReferralTaskVerificationComponent,
              ClientReferralTaskDetailsComponent,
              MatProgressSpinner]
})
export class ClientReferralTaskComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthenticationService);
  private titleService = inject(Title);
  private clientReferralTasksService = inject(ClientReferralTasksService);
  private userPresenceService = inject(UserPresenceService);

  task: ClientReferralTask;
  loading = true;
  formDisabled = true;

  constructor() {
    this.formDisabled = this.authService.isCaptureAdminUser;

    this.route.paramMap.subscribe(paramsMap => {
      this.handleRouteChange(paramsMap);
    });
  }

  @HostListener('window:beforeunload')
  onBeforeUnload(): void {
    this.leaveAndUnsubscribe();
  }

  ngOnInit() {
    this.loadTask(this.taskId);
  }

  ngOnDestroy(): void {
    this.leaveAndUnsubscribe();
  }

  onTaskChange(task: ClientReferralTask) {
    this.task = task;
  }

  onPrescriptionDismissed() {
    this.loadTask(this.taskId);
  }

  private loadTask(id) {
    this.loading = true;

    this.clientReferralTasksService.get(id).subscribe(
      task => {
        this.task = task;
        this.loading = false;
        this.updateTitle();
        this.subscribeAndJoin(this.task.id);
      },
      (err: HttpErrorResponse) => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        } else {
          console.error(err);
        }
      }
    );
  }

  private handleRouteChange(paramsMap) {
    const params = paramsMap['params'];
    const taskId = params && params['id'];

    if (shouldReloadTask(this.task, taskId)) {
      this.leaveAndUnsubscribe();
      this.loadTask(taskId);
    }
  }

  private updateTitle() {
    const title = `NuvemRX 340B Recovery - View Task | ${this.task.patient.mrn}`;
    this.titleService.setTitle(title);
  }

  private get taskId(): number {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  public get navBaseUrl(): string {
    return `/capture-admin/referral-claims/clients/client-referral-tasks/${this.taskId}`;
  }

  public get displayUserPresence(): boolean {
    return !this.authService.isCaptureAdminUser;
  }

  private subscribeAndJoin(id) {
    if (!this.displayUserPresence) return;
    this.userPresenceService.subscribeAndJoin(userPresenceKey, id);
  }

  private leaveAndUnsubscribe() {
    if (!this.displayUserPresence || !this.task) return;
    this.userPresenceService.leaveAndUnsubscribe(userPresenceKey, this.task.id);
  }
}
